<template>
  <div
    ref="canvasRoot"
    class="workspace-canvas workspace-theme"
    :class="{ 'is-editing': editMode, 'is-drop-target': dropActive, 'is-fullscreen': fullscreen }"
    :data-ws-theme="theme"
    :style="typographyStyle"
    @dragenter="onDragEnter"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <div ref="fitHost" class="workspace-canvas__fit">
      <template v-if="layout && layoutTabs.length">
        <div
          v-for="tab in layoutTabs"
          :key="tab.id"
          class="workspace-canvas__tab-pane"
          :class="{ 'is-active': tab.id === activeTabId }"
          :aria-hidden="tab.id !== activeTabId"
        >
          <grid-layout
            v-if="gridLayoutFor(tab.id).length"
            :layout="gridLayoutFor(tab.id)"
            :col-num="colNum"
            :row-height="effectiveRowHeight"
            :is-draggable="editMode && tab.id === activeTabId"
            :is-resizable="editMode && tab.id === activeTabId"
            :vertical-compact="false"
            :prevent-collision="true"
            :restore-on-drag="true"
            :margin="[10, 10]"
            :use-css-transforms="true"
            @layout-updated="onLayoutUpdated($event, tab.id)"
          >
            <grid-item
              v-for="item in gridLayoutFor(tab.id)"
              :key="`${tab.id}-${item.i}`"
              :x="item.x"
              :y="item.y"
              :w="item.w"
              :h="item.h"
              :i="item.i"
              :min-w="item.minW || 2"
              :min-h="item.minH || 2"
              drag-allow-from=".widget-wrapper__header"
              drag-ignore-from=".widget-wrapper__actions,a,button,input,textarea,select"
            >
              <WidgetWrapper
                v-if="widgetMapFor(tab)[item.i]"
                :title="getTitle(widgetMapFor(tab)[item.i])"
                :edit-mode="editMode && tab.id === activeTabId"
                :flush="isFlush(widgetMapFor(tab)[item.i].type)"
                :config="widgetMapFor(tab)[item.i].config || {}"
                @settings="openSettings(widgetMapFor(tab)[item.i])"
                @remove="removeWidget(item.i)"
              >
                <component
                  :is="getComponent(widgetMapFor(tab)[item.i].type)"
                  v-if="getComponent(widgetMapFor(tab)[item.i].type)"
                  :config="widgetMapFor(tab)[item.i].config || {}"
                  :widget-id="item.i"
                  :tab-active="tab.id === activeTabId"
                />
                <div v-else class="text-muted">Unknown widget</div>
              </WidgetWrapper>
            </grid-item>
          </grid-layout>
          <div
            v-else-if="tab.id === activeTabId"
            class="workspace-canvas__empty text-center text-muted py-5"
          >
            {{ editMode ? 'Drag a widget here from the palette' : 'No widgets yet — enable Edit Layout to add some.' }}
          </div>
        </div>
      </template>
      <div v-else-if="!layout" class="text-center text-muted py-5">Loading workspace...</div>
    </div>
    <div v-if="dropActive" class="workspace-canvas__drop-hint">Drop to add widget</div>
    <WidgetSettingsModal ref="settingsModal" />
  </div>
</template>

<script>
import { GridLayout, GridItem } from 'vue-grid-layout'
import { mapGetters, mapActions } from 'vuex'
import WidgetWrapper from './WidgetWrapper.vue'
import WidgetSettingsModal from './WidgetSettingsModal.vue'
import { getWidgetComponent, getWidgetTitle, WIDGET_REGISTRY } from '@/widgets/registry'

const WIDGET_DND_MIME = 'application/x-controllabs-widget'
const GRID_MARGIN = 10
const MIN_FS_ROW_HEIGHT = 18
const UI_REF_W = 1200
const UI_REF_COLS = 12
const REF_COL_W = (UI_REF_W - GRID_MARGIN * (UI_REF_COLS + 1)) / UI_REF_COLS
const SCALE_MIN = 0.42
const SCALE_MAX = 2
const FS_SCALE_MIN = 0.35
const FS_SCALE_MAX = 2.5
const BASE_FONT = 14
const BASE_DISPLAY = 32

function toGridItem(w) {
  const minSize = WIDGET_REGISTRY[w.type]?.minSize || { w: 2, h: 2 }
  const lay = w.layout || {}
  return {
    i: w.id,
    x: Number(lay.x) || 0,
    y: Number(lay.y) || 0,
    w: Number(lay.w) || 3,
    h: Number(lay.h) || 2,
    // Always use registry mins so saved larger mins don't lock resize
    minW: minSize.w || 2,
    minH: minSize.h || 2,
  }
}

function isPaletteWidgetDrag(e) {
  if (typeof document !== 'undefined' && document.body.classList.contains('ws-widget-dragging')) {
    return true
  }
  const types = e.dataTransfer?.types
  if (!types) return false
  if (typeof types.contains === 'function') return types.contains(WIDGET_DND_MIME)
  return Array.from(types).includes(WIDGET_DND_MIME)
}

export default {
  components: { GridLayout, GridItem, WidgetWrapper, WidgetSettingsModal },
  props: {
    fullscreen: { type: Boolean, default: false },
  },
  data() {
    return {
      gridByTab: {},
      ready: false,
      saveTimer: null,
      ignoreLayoutEvents: false,
      dropActive: false,
      dragDepth: 0,
      fitHeight: 0,
      canvasWidth: 0,
      viewportWidth: 0,
      viewportHeight: 0,
      resizeObserver: null,
    }
  },
  computed: {
    ...mapGetters('workspace', ['layout', 'editMode', 'theme', 'activeTabId', 'layoutTabs']),
    ...mapGetters('mission', ['nodes']),
    activeGridLayout() {
      return this.gridByTab[this.activeTabId] || []
    },
    /** Sync grids when widgets are added/removed — not on tab switch. */
    layoutTabsKey() {
      return (this.layoutTabs || []).map((t) => (
        `${t.id}|${(t.widgets || []).map((w) => w.id).join(',')}`
      )).join(';')
    },
    colNum() {
      return this.layout?.gridCols || 12
    },
    baseRowHeight() {
      return this.layout?.rowHeight || 60
    },
    gridExtentRows() {
      const grid = this.activeGridLayout
      if (!grid.length) return 1
      return Math.max(...grid.map((item) => item.y + item.h))
    },
    gridColWidth() {
      const w = this.canvasWidth || this.viewportWidth || UI_REF_W
      return Math.max(18, (w - GRID_MARGIN * (this.colNum + 1)) / this.colNum)
    },
    colScale() {
      return this.gridColWidth / REF_COL_W
    },
    uiScale() {
      const col = Math.max(SCALE_MIN, Math.min(SCALE_MAX, this.colScale))
      if (!this.fullscreen || !this.activeGridLayout.length) {
        return col
      }
      if (!this.fitHeight) return col
      const fs = this.fullscreenFitScale
      return Math.max(SCALE_MIN, Math.min(SCALE_MAX, Math.max(col, fs)))
    },
    fullscreenFitScale() {
      if (!this.fullscreen || !this.fitHeight || !this.activeGridLayout.length) return this.colScale
      const rows = this.gridExtentRows
      const base = this.baseRowHeight
      const margins = GRID_MARGIN * (rows + 1)
      const contentH = rows * base + margins
      if (contentH <= 0) return this.colScale
      const heightScale = this.fitHeight / contentH
      const scale = Math.min(heightScale, this.colScale * 1.1)
      return Math.max(FS_SCALE_MIN, Math.min(FS_SCALE_MAX, scale))
    },
    typographyStyle() {
      const s = this.uiScale
      return {
        '--ws-ui-scale': s.toFixed(3),
        '--ws-content-font-size': `${(BASE_FONT * s).toFixed(2)}px`,
        '--ws-content-display-size': `${(BASE_DISPLAY * s).toFixed(2)}px`,
      }
    },
    effectiveRowHeight() {
      const base = this.baseRowHeight
      if (!this.fullscreen || !this.fitHeight || !this.activeGridLayout.length) {
        return base
      }
      return Math.max(MIN_FS_ROW_HEIGHT, Math.round(base * this.fullscreenFitScale))
    },
  },
  watch: {
    layoutTabsKey: {
      immediate: true,
      handler() {
        this.syncGridsFromStore()
      },
    },
    fullscreen() {
      this.$nextTick(() => this.refreshGridMetrics())
    },
    activeTabId(id) {
      this.onTabShown(id)
    },
    editMode() {
      this.$nextTick(() => this.refreshGridMetrics())
    },
    gridExtentRows() {
      if (this.fullscreen) this.$nextTick(() => this.refreshGridMetrics())
    },
  },
  mounted() {
    this.setupResizeObserver()
    window.addEventListener('resize', this.remeasure, { passive: true })
  },
  beforeDestroy() {
    if (this.saveTimer) clearTimeout(this.saveTimer)
    window.removeEventListener('resize', this.remeasure)
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
  },
  methods: {
    ...mapActions('workspace', ['updateLayoutFromGrid', 'removeWidget', 'addWidget']),
    setupResizeObserver() {
      const root = this.$refs.canvasRoot
      const host = this.$refs.fitHost
      const control = root?.closest('.mission-control')
      if (typeof ResizeObserver === 'undefined') {
        this.remeasure()
        return
      }
      this.resizeObserver = new ResizeObserver(() => this.remeasure())
      if (root) this.resizeObserver.observe(root)
      if (host && host !== root) this.resizeObserver.observe(host)
      if (control && control !== root) this.resizeObserver.observe(control)
      this.remeasure()
    },
    remeasure() {
      this.viewportWidth = window.innerWidth || 0
      this.viewportHeight = window.innerHeight || 0
      const el = this.$refs.canvasRoot
      const host = this.$refs.fitHost
      if (!el) return
      this.canvasWidth = el.clientWidth || 0
      this.fitHeight = (this.fullscreen && host ? host.clientHeight : el.clientHeight) || 0
    },
    getComponent(type) {
      return getWidgetComponent(type)
    },
    isFlush(type) {
      return type === 'youtube' || type === 'stream-health' || type === 'obs-program'
    },
    getTitle(widget) {
      return getWidgetTitle(widget.type, widget.config || {}, this.nodes)
    },
    gridLayoutFor(tabId) {
      return this.gridByTab[tabId] || []
    },
    widgetMapFor(tab) {
      const map = Object.create(null)
      ;(tab.widgets || []).forEach((w) => { map[w.id] = w })
      return map
    },
    syncGridsFromStore() {
      this.ignoreLayoutEvents = true
      const tabs = this.layoutTabs || []
      const next = { ...this.gridByTab }

      tabs.forEach((tab) => {
        const existing = next[tab.id] || []
        const byId = Object.create(null)
        existing.forEach((g) => { byId[g.i] = g })

        const grid = (tab.widgets || []).map((w) => {
          const minSize = WIDGET_REGISTRY[w.type]?.minSize || { w: 2, h: 2 }
          if (byId[w.id]) {
            byId[w.id].minW = minSize.w || 2
            byId[w.id].minH = minSize.h || 2
            return byId[w.id]
          }
          return toGridItem(w)
        })
        this.$set(next, tab.id, grid)
      })

      Object.keys(next).forEach((tabId) => {
        if (!tabs.some((t) => t.id === tabId)) {
          this.$delete(next, tabId)
        }
      })

      this.gridByTab = next
      this.ready = tabs.length > 0

      this.$nextTick(() => {
        this.ignoreLayoutEvents = false
        this.refreshGridMetrics()
      })
    },
    hydrateGridFromStore(tabId) {
      if (!tabId) return
      const tab = (this.layoutTabs || []).find((t) => t.id === tabId)
      if (!tab) return
      this.$set(this.gridByTab, tabId, (tab.widgets || []).map((w) => toGridItem(w)))
    },
    onTabShown(tabId) {
      this.ignoreLayoutEvents = true
      this.hydrateGridFromStore(tabId)
      this.$nextTick(() => {
        this.refreshGridMetrics()
        requestAnimationFrame(() => {
          this.refreshGridMetrics()
          this.ignoreLayoutEvents = false
        })
      })
    },
    refreshGridMetrics() {
      this.remeasure()
      window.dispatchEvent(new Event('resize'))
    },
    openSettings(widget) {
      if (!widget) return
      this.$refs.settingsModal.open(widget)
    },
    onLayoutUpdated(newLayout, tabId) {
      if (tabId !== this.activeTabId) return
      // Skip mount/sync noise — only persist real user drag/resize when editing
      if (this.ignoreLayoutEvents || !this.editMode) return
      if (!Array.isArray(newLayout) || !newLayout.length) return
      if ((this.canvasWidth || 0) < 80) return

      if (this.saveTimer) clearTimeout(this.saveTimer)
      this.saveTimer = setTimeout(() => {
        if (this.ignoreLayoutEvents || !this.editMode) return
        this.updateLayoutFromGrid(newLayout)
      }, 400)
    },
    onDragEnter(e) {
      if (!this.editMode || !isPaletteWidgetDrag(e)) return
      e.preventDefault()
      this.dragDepth += 1
      this.dropActive = true
    },
    onDragOver(e) {
      if (!this.editMode || !isPaletteWidgetDrag(e)) return
      e.preventDefault()
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
      this.dropActive = true
    },
    onDragLeave() {
      if (!this.editMode) return
      this.dragDepth = Math.max(0, this.dragDepth - 1)
      if (this.dragDepth === 0) this.dropActive = false
    },
    onDrop(e) {
      if (!this.editMode) return
      e.preventDefault()
      this.dragDepth = 0
      this.dropActive = false

      const type = e.dataTransfer?.getData(WIDGET_DND_MIME)
        || e.dataTransfer?.getData('text/plain')
      if (!type || !WIDGET_REGISTRY[type]) return

      const { x, y } = this.clientToGrid(e.clientX, e.clientY)
      this.addWidget({ type, config: {}, x, y })
    },
    clientToGrid(clientX, clientY) {
      const rect = this.$el.getBoundingClientRect()
      const style = window.getComputedStyle(this.$el)
      const padL = parseFloat(style.paddingLeft) || 0
      const padT = parseFloat(style.paddingTop) || 0
      const innerW = Math.max(1, rect.width - padL - (parseFloat(style.paddingRight) || 0))
      const colWidth = (innerW - GRID_MARGIN * (this.colNum + 1)) / this.colNum
      const relX = clientX - rect.left - padL - GRID_MARGIN
      const relY = clientY - rect.top - padT - GRID_MARGIN
      const x = Math.max(0, Math.min(this.colNum - 1, Math.floor(relX / (colWidth + GRID_MARGIN))))
      const y = Math.max(0, Math.floor(relY / (this.effectiveRowHeight + GRID_MARGIN)))
      return { x, y }
    },
  },
}
</script>

<style scoped>
.workspace-canvas {
  min-height: 600px;
  background: var(--ws-canvas-bg);
  border-radius: 8px;
  padding: 4px;
  position: relative;
  transition: box-shadow 0.15s ease, outline-color 0.15s ease;
}
.workspace-canvas.is-fullscreen {
  min-height: 0;
  height: 100%;
  border-radius: 0;
  padding: 0;
}
.workspace-canvas__fit {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
}
.workspace-canvas__tab-pane {
  width: 100%;
  min-height: 0;
}
.workspace-canvas__tab-pane:not(.is-active) {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  visibility: hidden;
  pointer-events: none;
  z-index: 0;
}
.workspace-canvas__tab-pane.is-active {
  position: relative;
  z-index: 1;
}
.workspace-canvas.is-drop-target {
  outline: 2px dashed var(--ws-accent, #128c7e);
  outline-offset: -2px;
  box-shadow: inset 0 0 0 9999px rgba(18, 140, 126, 0.06);
}
.workspace-canvas__drop-hint {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 40;
  pointer-events: none;
  padding: 0.55rem 0.9rem;
  border-radius: 8px;
  background: rgba(18, 140, 126, 0.92);
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.01em;
}
.workspace-canvas__empty {
  pointer-events: none;
}
.workspace-canvas >>> .vue-grid-item {
  touch-action: none;
}
.workspace-canvas >>> .vue-grid-item > .widget-wrapper {
  height: 100%;
  width: 100%;
  position: relative;
  z-index: 1;
  min-height: 0;
}
.workspace-canvas.is-fullscreen >>> .widget-wrapper__body {
  overflow: auto;
  min-height: 0;
}
.workspace-canvas >>> .vue-grid-item > .vue-resizable-handle {
  position: absolute !important;
  right: 0 !important;
  bottom: 0 !important;
  z-index: 30 !important;
  width: 22px !important;
  height: 22px !important;
  pointer-events: auto !important;
  cursor: se-resize !important;
  opacity: 0;
}
.workspace-canvas.is-editing >>> .vue-grid-item {
  outline: 1px dashed rgba(18, 140, 126, 0.5);
  outline-offset: -1px;
}
.workspace-canvas.is-editing >>> .vue-grid-item > .vue-resizable-handle {
  opacity: 1 !important;
  background-color: rgba(18, 140, 126, 0.25) !important;
  border-radius: 4px 0 6px 0;
  background-image: none !important;
}
.workspace-canvas.is-editing >>> .vue-grid-item > .vue-resizable-handle::after {
  content: '';
  position: absolute;
  right: 4px;
  bottom: 4px;
  width: 10px;
  height: 10px;
  border-right: 2px solid #128c7e;
  border-bottom: 2px solid #128c7e;
}

@media (max-width: 1199.98px) {
  .workspace-canvas {
    min-height: 480px;
    padding: 2px;
  }
  .workspace-canvas.is-editing >>> .vue-grid-item > .vue-resizable-handle {
    width: 28px !important;
    height: 28px !important;
  }
  .workspace-canvas.is-editing >>> .vue-grid-item > .vue-resizable-handle::after {
    right: 6px;
    bottom: 6px;
    width: 12px;
    height: 12px;
  }
}

@media (max-width: 767.98px) {
  .workspace-canvas {
    min-height: 360px;
  }
}
</style>
